/**
 * ComplaintsPage - Full Complaint Workflow Management
 * Complaint → AI Analysis → Investigation → Enforcement
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import AdminSidebar from "../components/layout/AdminSidebar";
import GovernanceNotice from "../components/GovernanceNotice";
import FileUploadZone from "../components/FileUploadZone";
import AIDocumentAnalysis from "../components/AIDocumentAnalysis";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Bot,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Clock,
  Eye,
  FileText,
  Filter,
  Gavel,
  Loader2,
  Mail,
  MessageSquare,
  Plus,
  Search,
  Send,
  Shield,
  Sparkles,
  Target,
  Upload,
  User,
  Users,
  XCircle,
  ClipboardList,
  Scale,
  FileCheck,
  AlertCircle,
  Building,
  Calendar,
  ExternalLink
} from "lucide-react";

// Complaint categories
const COMPLAINT_CATEGORIES = [
  { value: "neighbor_violation", label: "Neighbor Violation", icon: <Users className="w-4 h-4" /> },
  { value: "architectural", label: "Architectural/DRC", icon: <Building className="w-4 h-4" /> },
  { value: "board_conduct", label: "Board Conduct", icon: <Shield className="w-4 h-4" /> },
  { value: "safety", label: "Safety Concern", icon: <AlertTriangle className="w-4 h-4" /> },
  { value: "nuisance", label: "Nuisance", icon: <AlertCircle className="w-4 h-4" /> },
  { value: "maintenance", label: "Maintenance", icon: <ClipboardList className="w-4 h-4" /> },
  { value: "financial", label: "Financial", icon: <Scale className="w-4 h-4" /> },
  { value: "property_manager", label: "Property Manager", icon: <User className="w-4 h-4" /> },
  { value: "other", label: "Other", icon: <FileText className="w-4 h-4" /> }
];

// Complaint statuses with styling
const STATUS_CONFIG = {
  open: { label: "Open", color: "bg-blue-500/10 text-blue-600 border-blue-200", icon: <Clock className="w-3 h-3" /> },
  under_review: { label: "Under Review", color: "bg-yellow-500/10 text-yellow-600 border-yellow-200", icon: <Eye className="w-3 h-3" /> },
  investigation: { label: "Investigation", color: "bg-purple-500/10 text-purple-600 border-purple-200", icon: <Search className="w-3 h-3" /> },
  verified: { label: "Verified", color: "bg-green-500/10 text-green-600 border-green-200", icon: <CheckCircle className="w-3 h-3" /> },
  unverified: { label: "Unverified", color: "bg-gray-500/10 text-gray-600 border-gray-200", icon: <XCircle className="w-3 h-3" /> },
  escalated: { label: "Escalated", color: "bg-red-500/10 text-red-600 border-red-200", icon: <AlertTriangle className="w-3 h-3" /> },
  closed: { label: "Closed", color: "bg-slate-500/10 text-slate-600 border-slate-200", icon: <FileCheck className="w-3 h-3" /> }
};

// Validity styling
const VALIDITY_CONFIG = {
  supported: { label: "Supported", color: "bg-green-100 text-green-700" },
  unsupported: { label: "Unsupported", color: "bg-red-100 text-red-700" },
  partially_supported: { label: "Partially Supported", color: "bg-yellow-100 text-yellow-700" },
  outside_authority: { label: "Outside Authority", color: "bg-gray-100 text-gray-700" },
  requires_investigation: { label: "Requires Investigation", color: "bg-purple-100 text-purple-700" }
};

const ComplaintsPage = ({ embedded = false }) => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [complaints, setComplaints] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Selected complaint detail view
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTab, setDetailTab] = useState("overview");
  
  // Modals
  const [createOpen, setCreateOpen] = useState(false);
  const [analyzeLoading, setAnalyzeLoading] = useState(false);
  const [investigationOpen, setInvestigationOpen] = useState(false);
  const [completeInvestigationOpen, setCompleteInvestigationOpen] = useState(false);
  const [createViolationOpen, setCreateViolationOpen] = useState(false);
  
  // Form states
  const [createForm, setCreateForm] = useState({
    submitted_by_owner_id: "",
    complainant_name_text: "",
    target_type: "owner",
    target_owner_id: "",
    target_name_text: "",
    target_property_id: "",
    target_property_text: "",
    target_role: "",
    category: "",
    subject: "",
    description: "",
    lot_number: ""
  });
  const [complainantMode, setComplainantMode] = useState("select"); // "select" | "type"
  const [targetMode, setTargetMode] = useState("select");
  const [propertyMode, setPropertyMode] = useState("select");
  
  const [investigationForm, setInvestigationForm] = useState({
    assigned_to: "",
    tasks: []
  });
  
  const [completeForm, setCompleteForm] = useState({
    findings: "",
    findings_notes: "",
    recommended_action: "",
    recommendation_notes: ""
  });
  
  // Reference data
  const [owners, setOwners] = useState([]);
  const [properties, setProperties] = useState([]);
  const [escalationLadder, setEscalationLadder] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [governanceAcknowledged, setGovernanceAcknowledged] = useState(false);

  // Fetch data
  useEffect(() => {
    fetchComplaints();
    fetchStats();
    fetchReferenceData();
    fetchEscalationLadder();
  }, [token]);

  const fetchComplaints = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (categoryFilter !== "all") params.append("category", categoryFilter);
      
      const res = await axios.get(`${API}/complaints?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComplaints(res.data);
    } catch (error) {
      toast.error("Failed to load complaints");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/complaints/stats/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const fetchReferenceData = async () => {
    try {
      const [ownersRes, propertiesRes] = await Promise.all([
        axios.get(`${API}/owners`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/properties`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setOwners(ownersRes.data || []);
      setProperties(propertiesRes.data || []);
    } catch (error) {
      console.error("Failed to fetch reference data:", error);
    }
  };

  const fetchEscalationLadder = async () => {
    try {
      const res = await axios.get(`${API}/complaints/escalation-ladder`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEscalationLadder(res.data);
    } catch (error) {
      console.error("Failed to fetch escalation ladder:", error);
    }
  };

  const fetchComplaintDetail = async (complaintId) => {
    try {
      setDetailLoading(true);
      const res = await axios.get(`${API}/complaints/${complaintId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedComplaint(res.data);
      setDetailTab("overview");
    } catch (error) {
      toast.error("Failed to load complaint details");
    } finally {
      setDetailLoading(false);
    }
  };

  // Actions
  const handleCreateComplaint = async () => {
    try {
      const payload = {
        submitted_by_owner_id: complainantMode === "select" ? createForm.submitted_by_owner_id : "",
        complainant_name_text: complainantMode === "type" ? createForm.complainant_name_text : null,
        submitted_against: [{
          target_type: createForm.target_type,
          owner_id: createForm.target_type === "owner" && targetMode === "select" ? createForm.target_owner_id : null,
          property_id: propertyMode === "select" ? (createForm.target_property_id || null) : null,
          role_name: createForm.target_type !== "owner" ? createForm.target_role : null,
          target_name_text: targetMode === "type" ? createForm.target_name_text : null
        }],
        category: createForm.category,
        subject: createForm.subject,
        description: createForm.description,
        lot_number: createForm.lot_number || null
      };
      
      const res = await axios.post(`${API}/complaints`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const complaintId = res.data.complaint_id;
      
      // Upload files if any
      if (pendingFiles.length > 0) {
        const formData = new FormData();
        pendingFiles.forEach(f => formData.append("files", f.file));
        try {
          await axios.post(`${API}/file-uploads/complaint/${complaintId}`, formData, {
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
          });
          toast.success(`Complaint created with ${pendingFiles.length} file(s)`);
        } catch (uploadErr) {
          toast.warning("Complaint created but some files failed to upload");
        }
      } else {
        toast.success("Complaint created successfully");
      }
      
      setCreateOpen(false);
      setCreateForm({
        submitted_by_owner_id: "",
        complainant_name_text: "",
        target_type: "owner",
        target_owner_id: "",
        target_name_text: "",
        target_property_id: "",
        target_property_text: "",
        target_role: "",
        category: "",
        subject: "",
        description: "",
        lot_number: ""
      });
      setPendingFiles([]);
      setGovernanceAcknowledged(false);
      fetchComplaints();
      fetchStats();
      
      // Open the new complaint
      fetchComplaintDetail(complaintId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create complaint");
    }
  };

  const handleAnalyzeComplaint = async () => {
    if (!selectedComplaint) return;
    
    try {
      setAnalyzeLoading(true);
      const res = await axios.post(
        `${API}/complaints/${selectedComplaint.complaint_id}/analyze`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("AI analysis complete");
      fetchComplaintDetail(selectedComplaint.complaint_id);
      fetchComplaints();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Analysis failed");
    } finally {
      setAnalyzeLoading(false);
    }
  };

  const handleApproveAnalysis = async (approved) => {
    if (!selectedComplaint) return;
    
    try {
      await axios.post(
        `${API}/complaints/${selectedComplaint.complaint_id}/approve-analysis`,
        { approved, notes: "" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(approved ? "Analysis approved" : "Analysis rejected");
      fetchComplaintDetail(selectedComplaint.complaint_id);
    } catch (error) {
      toast.error("Failed to update approval status");
    }
  };

  const handleAssignInvestigation = async () => {
    if (!selectedComplaint) return;
    
    try {
      await axios.post(
        `${API}/complaints/${selectedComplaint.complaint_id}/investigation/assign`,
        {
          assigned_to: investigationForm.assigned_to,
          initial_tasks: investigationForm.tasks
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Investigation assigned");
      setInvestigationOpen(false);
      fetchComplaintDetail(selectedComplaint.complaint_id);
      fetchComplaints();
    } catch (error) {
      toast.error("Failed to assign investigation");
    }
  };

  const handleStartInvestigation = async () => {
    if (!selectedComplaint) return;
    
    try {
      await axios.post(
        `${API}/complaints/${selectedComplaint.complaint_id}/investigation/start`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Investigation started");
      fetchComplaintDetail(selectedComplaint.complaint_id);
    } catch (error) {
      toast.error("Failed to start investigation");
    }
  };

  const handleCompleteInvestigation = async () => {
    if (!selectedComplaint) return;
    
    try {
      await axios.post(
        `${API}/complaints/${selectedComplaint.complaint_id}/investigation/complete`,
        completeForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Investigation completed");
      setCompleteInvestigationOpen(false);
      fetchComplaintDetail(selectedComplaint.complaint_id);
      fetchComplaints();
    } catch (error) {
      toast.error("Failed to complete investigation");
    }
  };

  const handleCloseComplaint = async (reason) => {
    if (!selectedComplaint) return;
    
    try {
      await axios.post(
        `${API}/complaints/${selectedComplaint.complaint_id}/close?closure_reason=${encodeURIComponent(reason)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Complaint closed");
      fetchComplaintDetail(selectedComplaint.complaint_id);
      fetchComplaints();
      fetchStats();
    } catch (error) {
      toast.error("Failed to close complaint");
    }
  };

  // Filter complaints
  const filteredComplaints = complaints.filter(c => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        c.subject?.toLowerCase().includes(query) ||
        c.complaint_id?.toLowerCase().includes(query) ||
        c.complainant_info?.first_name?.toLowerCase().includes(query) ||
        c.complainant_info?.last_name?.toLowerCase().includes(query) ||
        c.complainant_name_text?.toLowerCase().includes(query) ||
        c.lot_number?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Render complaint card
  const renderComplaintCard = (complaint) => {
    const status = STATUS_CONFIG[complaint.status] || STATUS_CONFIG.open;
    const category = COMPLAINT_CATEGORIES.find(c => c.value === complaint.category);
    
    return (
      <Card 
        key={complaint.complaint_id}
        className={`cursor-pointer transition-all hover:shadow-md ${
          selectedComplaint?.complaint_id === complaint.complaint_id ? "ring-2 ring-primary" : ""
        }`}
        onClick={() => fetchComplaintDetail(complaint.complaint_id)}
        data-testid={`complaint-card-${complaint.complaint_id}`}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Badge className={status.color} variant="outline">
                  {status.icon}
                  <span className="ml-1">{status.label}</span>
                </Badge>
                {complaint.has_ai_analysis && (
                  <Badge variant="outline" className="bg-purple-50 text-purple-600 border-purple-200">
                    <Sparkles className="w-3 h-3 mr-1" />
                    AI Analyzed
                  </Badge>
                )}
              </div>
              <h4 className="font-medium text-sm truncate">{complaint.subject}</h4>
              <p className="text-xs text-muted-foreground mt-1">
                {complaint.complainant_name_text || (complaint.complainant_info ? `${complaint.complainant_info.first_name} ${complaint.complainant_info.last_name}` : "Unknown")} {complaint.lot_number ? `• Lot ${complaint.lot_number}` : ""} • {new Date(complaint.created_at).toLocaleDateString()}
              </p>
            </div>
            {category && (
              <div className="text-muted-foreground">
                {category.icon}
              </div>
            )}
          </div>
          
          {/* Progress indicators */}
          <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
            {complaint.has_investigation && (
              <span className="flex items-center gap-1">
                <Search className="w-3 h-3" />
                Investigation
              </span>
            )}
            {complaint.linked_violations_count > 0 && (
              <span className="flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {complaint.linked_violations_count} violation(s)
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  const pageContent = (
      <main className="flex-1 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Complaints Management</h1>
            <p className="text-muted-foreground">
              Handle complaints with AI analysis, investigation, and enforcement
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate("/admin/complaints/analytics")} data-testid="analytics-btn">
              <Target className="w-4 h-4 mr-2" />
              Analytics
            </Button>
            <Button onClick={() => setCreateOpen(true)} data-testid="create-complaint-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Complaint
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="text-2xl font-bold">{stats.total}</div>
                <p className="text-xs text-muted-foreground">Total Complaints</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-blue-600">{stats.by_status?.open || 0}</div>
                <p className="text-xs text-muted-foreground">Open</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-purple-600">{stats.by_status?.investigation || 0}</div>
                <p className="text-xs text-muted-foreground">Under Investigation</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-green-600">{stats.by_status?.verified || 0}</div>
                <p className="text-xs text-muted-foreground">Verified</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-slate-600">{stats.by_status?.closed || 0}</div>
                <p className="text-xs text-muted-foreground">Closed</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Complaints List */}
          <div className="lg:col-span-1">
            <Card className="h-[calc(100vh-300px)]">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Complaints</CardTitle>
                  <Badge variant="outline">{filteredComplaints.length}</Badge>
                </div>
                
                {/* Filters */}
                <div className="flex gap-2 mt-2">
                  <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); fetchComplaints(); }}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={categoryFilter} onValueChange={(v) => { setCategoryFilter(v); fetchComplaints(); }}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {COMPLAINT_CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Search */}
                <div className="relative mt-2">
                  <Search className="absolute left-2 top-2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search complaints..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-8 h-8 text-sm"
                  />
                </div>
              </CardHeader>
              
              <CardContent className="p-2">
                <ScrollArea className="h-[calc(100vh-500px)]">
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    </div>
                  ) : filteredComplaints.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No complaints found</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {filteredComplaints.map(renderComplaintCard)}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Complaint Detail */}
          <div className="lg:col-span-2">
            {selectedComplaint ? (
              <ComplaintDetailView
                complaint={selectedComplaint}
                loading={detailLoading}
                activeTab={detailTab}
                setActiveTab={setDetailTab}
                onAnalyze={handleAnalyzeComplaint}
                analyzeLoading={analyzeLoading}
                onApproveAnalysis={handleApproveAnalysis}
                onAssignInvestigation={() => setInvestigationOpen(true)}
                onStartInvestigation={handleStartInvestigation}
                onCompleteInvestigation={() => setCompleteInvestigationOpen(true)}
                onClose={handleCloseComplaint}
                escalationLadder={escalationLadder}
                token={token}
                onRefresh={() => fetchComplaintDetail(selectedComplaint.complaint_id)}
              />
            ) : (
              <Card className="h-[calc(100vh-300px)] flex items-center justify-center">
                <div className="text-center text-muted-foreground">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Select a complaint to view details</p>
                </div>
              </Card>
            )}
          </div>
        </div>

        {/* Create Complaint Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Complaint</DialogTitle>
              <DialogDescription>
                Enter details about the complaint to begin the review process.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Governance Notice */}
              <GovernanceNotice
                location="enforcement"
                requireAcknowledgment={true}
                onAcknowledged={() => setGovernanceAcknowledged(true)}
                compact={true}
              />

              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Complainant (Submitted By)</Label>
                  <button type="button" className="text-xs text-primary hover:underline" onClick={() => setComplainantMode(m => m === "select" ? "type" : "select")} data-testid="complainant-mode-toggle">
                    {complainantMode === "select" ? "Type name instead" : "Select from list"}
                  </button>
                </div>
                {complainantMode === "select" ? (
                  <Select
                    value={createForm.submitted_by_owner_id}
                    onValueChange={(v) => setCreateForm({...createForm, submitted_by_owner_id: v})}
                  >
                    <SelectTrigger data-testid="complainant-select">
                      <SelectValue placeholder="Select owner who submitted complaint" />
                    </SelectTrigger>
                    <SelectContent>
                      {owners.map(owner => (
                        <SelectItem key={owner.owner_id} value={owner.owner_id}>
                          {owner.first_name} {owner.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={createForm.complainant_name_text}
                    onChange={(e) => setCreateForm({...createForm, complainant_name_text: e.target.value})}
                    placeholder="Enter complainant's full name"
                    data-testid="complainant-name-input"
                  />
                )}
              </div>
              
              <div>
                <Label>Complaint Against</Label>
                <Select
                  value={createForm.target_type}
                  onValueChange={(v) => setCreateForm({...createForm, target_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="owner">Specific Owner</SelectItem>
                    <SelectItem value="hoa_board">HOA Board</SelectItem>
                    <SelectItem value="drc">Design Review Committee</SelectItem>
                    <SelectItem value="property_manager">Property Manager</SelectItem>
                    <SelectItem value="general_hoa">General HOA</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {createForm.target_type === "owner" && (
                <div className="space-y-3">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label>Target Owner</Label>
                      <button type="button" className="text-xs text-primary hover:underline" onClick={() => setTargetMode(m => m === "select" ? "type" : "select")} data-testid="target-mode-toggle">
                        {targetMode === "select" ? "Type name instead" : "Select from list"}
                      </button>
                    </div>
                    {targetMode === "select" ? (
                      <Select
                        value={createForm.target_owner_id}
                        onValueChange={(v) => setCreateForm({...createForm, target_owner_id: v})}
                      >
                        <SelectTrigger data-testid="target-owner-select">
                          <SelectValue placeholder="Select owner" />
                        </SelectTrigger>
                        <SelectContent>
                          {owners.map(owner => (
                            <SelectItem key={owner.owner_id} value={owner.owner_id}>
                              {owner.first_name} {owner.last_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        value={createForm.target_name_text}
                        onChange={(e) => setCreateForm({...createForm, target_name_text: e.target.value})}
                        placeholder="Enter target owner's full name"
                        data-testid="target-name-input"
                      />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label>Property (Optional)</Label>
                      <button type="button" className="text-xs text-primary hover:underline" onClick={() => setPropertyMode(m => m === "select" ? "type" : "select")} data-testid="property-mode-toggle">
                        {propertyMode === "select" ? "Type address instead" : "Select from list"}
                      </button>
                    </div>
                    {propertyMode === "select" ? (
                      <Select
                        value={createForm.target_property_id}
                        onValueChange={(v) => setCreateForm({...createForm, target_property_id: v})}
                      >
                        <SelectTrigger data-testid="target-property-select">
                          <SelectValue placeholder="Select property" />
                        </SelectTrigger>
                        <SelectContent>
                          {properties.map(prop => (
                            <SelectItem key={prop.property_id} value={prop.property_id}>
                              {prop.address || prop.lot_number}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        value={createForm.target_property_text}
                        onChange={(e) => setCreateForm({...createForm, target_property_text: e.target.value})}
                        placeholder="Enter property address"
                        data-testid="target-property-input"
                      />
                    )}
                  </div>
                </div>
              )}
              
              <div>
                <Label>Lot Number (Optional)</Label>
                <Input
                  value={createForm.lot_number}
                  onChange={(e) => setCreateForm({...createForm, lot_number: e.target.value})}
                  placeholder="e.g., Lot 23, Block 4"
                  data-testid="lot-number-input"
                />
              </div>
              
              <div>
                <Label>Category</Label>
                <Select
                  value={createForm.category}
                  onValueChange={(v) => setCreateForm({...createForm, category: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPLAINT_CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        <div className="flex items-center gap-2">
                          {cat.icon}
                          {cat.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Subject</Label>
                <Input
                  value={createForm.subject}
                  onChange={(e) => setCreateForm({...createForm, subject: e.target.value})}
                  placeholder="Brief description of the complaint"
                />
              </div>
              
              <div>
                <Label>Description</Label>
                <Textarea
                  value={createForm.description}
                  onChange={(e) => setCreateForm({...createForm, description: e.target.value})}
                  placeholder="Full details of the complaint..."
                  rows={4}
                />
              </div>

              {/* File Uploads */}
              <div>
                <Label>Attachments (Optional)</Label>
                <p className="text-xs text-muted-foreground mb-2">Upload photos, documents, or evidence. Up to 10 files, 25 MB each.</p>
                <FileUploadZone
                  entityType="complaint"
                  maxFiles={10}
                  maxFileSize={25 * 1024 * 1024}
                  maxTotalSize={100 * 1024 * 1024}
                  allowedExtensions={[".pdf", ".jpg", ".jpeg", ".png", ".heic", ".docx", ".doc", ".xlsx"]}
                  onFilesChange={setPendingFiles}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button
                onClick={handleCreateComplaint}
                disabled={!(complainantMode === "select" ? createForm.submitted_by_owner_id : createForm.complainant_name_text) || !createForm.category || !createForm.subject}
                data-testid="submit-complaint-btn"
              >
                Create Complaint
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Assign Investigation Dialog */}
        <Dialog open={investigationOpen} onOpenChange={setInvestigationOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Assign Investigation</DialogTitle>
              <DialogDescription>
                Assign an investigator and select initial tasks.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label>Assign To</Label>
                <Select
                  value={investigationForm.assigned_to}
                  onValueChange={(v) => setInvestigationForm({...investigationForm, assigned_to: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select investigator" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="property_manager">Property Manager</SelectItem>
                    <SelectItem value="board_member">Board Member</SelectItem>
                    <SelectItem value="drc_chair">DRC Chair</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Initial Tasks</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {[
                    { value: "site_visit", label: "Site Visit" },
                    { value: "evidence_review", label: "Evidence Review" },
                    { value: "interview_complainant", label: "Interview Complainant" },
                    { value: "interview_subject", label: "Interview Subject" },
                    { value: "drc_consult", label: "DRC Consultation" },
                    { value: "document_review", label: "Document Review" }
                  ].map(task => (
                    <label key={task.value} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={investigationForm.tasks.includes(task.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setInvestigationForm({
                              ...investigationForm,
                              tasks: [...investigationForm.tasks, task.value]
                            });
                          } else {
                            setInvestigationForm({
                              ...investigationForm,
                              tasks: investigationForm.tasks.filter(t => t !== task.value)
                            });
                          }
                        }}
                        className="rounded"
                      />
                      {task.label}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setInvestigationOpen(false)}>Cancel</Button>
              <Button onClick={handleAssignInvestigation} disabled={!investigationForm.assigned_to}>
                Assign Investigation
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Complete Investigation Dialog */}
        <Dialog open={completeInvestigationOpen} onOpenChange={setCompleteInvestigationOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Complete Investigation</DialogTitle>
              <DialogDescription>
                Record findings and recommended action.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label>Findings</Label>
                <Select
                  value={completeForm.findings}
                  onValueChange={(v) => setCompleteForm({...completeForm, findings: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select findings" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="verified">Verified - Violation confirmed</SelectItem>
                    <SelectItem value="partially_verified">Partially Verified</SelectItem>
                    <SelectItem value="unverified">Unverified - No violation found</SelectItem>
                    <SelectItem value="inconclusive">Inconclusive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Findings Notes</Label>
                <Textarea
                  value={completeForm.findings_notes}
                  onChange={(e) => setCompleteForm({...completeForm, findings_notes: e.target.value})}
                  placeholder="Document investigation findings..."
                  rows={3}
                />
              </div>
              
              <div>
                <Label>Recommended Action</Label>
                <Select
                  value={completeForm.recommended_action}
                  onValueChange={(v) => setCompleteForm({...completeForm, recommended_action: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select action" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no_action">No Action Required</SelectItem>
                    <SelectItem value="courtesy_notice">Courtesy Notice</SelectItem>
                    <SelectItem value="formal_notice">Formal Notice</SelectItem>
                    <SelectItem value="hearing_notice">Hearing Notice</SelectItem>
                    <SelectItem value="fine_notice">Fine Notice</SelectItem>
                    <SelectItem value="drc_referral">DRC Referral</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Recommendation Notes</Label>
                <Textarea
                  value={completeForm.recommendation_notes}
                  onChange={(e) => setCompleteForm({...completeForm, recommendation_notes: e.target.value})}
                  placeholder="Explain the recommendation..."
                  rows={2}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setCompleteInvestigationOpen(false)}>Cancel</Button>
              <Button
                onClick={handleCompleteInvestigation}
                disabled={!completeForm.findings || !completeForm.recommended_action}
              >
                Complete Investigation
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
  );

  if (embedded) return pageContent;
  return (
    <div className="flex min-h-screen bg-background">
      <AdminSidebar />
      {pageContent}
    </div>
  );
};

// Complaint Detail View Component
const ComplaintDetailView = ({
  complaint,
  loading,
  activeTab,
  setActiveTab,
  onAnalyze,
  analyzeLoading,
  onApproveAnalysis,
  onAssignInvestigation,
  onStartInvestigation,
  onCompleteInvestigation,
  onClose,
  escalationLadder,
  token,
  onRefresh
}) => {
  const status = STATUS_CONFIG[complaint.status] || STATUS_CONFIG.open;
  const category = COMPLAINT_CATEGORIES.find(c => c.value === complaint.category);
  
  if (loading) {
    return (
      <Card className="h-[calc(100vh-300px)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </Card>
    );
  }
  
  return (
    <Card className="h-[calc(100vh-300px)] flex flex-col">
      <CardHeader className="pb-3 border-b">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Badge className={status.color} variant="outline">
                {status.icon}
                <span className="ml-1">{status.label}</span>
              </Badge>
              {category && (
                <Badge variant="outline">
                  {category.icon}
                  <span className="ml-1">{category.label}</span>
                </Badge>
              )}
            </div>
            <CardTitle className="text-lg">{complaint.subject}</CardTitle>
            <CardDescription>
              ID: {complaint.complaint_id} • Created {new Date(complaint.created_at).toLocaleDateString()}
            </CardDescription>
          </div>
          
          {/* Action buttons based on status */}
          <div className="flex gap-2">
            <AIDocumentAnalysis
              context={`Complaint: ${complaint.subject} - ${complaint.description?.substring(0, 200)}`}
              analysisType="complaint_review"
              buttonText="Analyze Doc"
              buttonVariant="outline"
            />
            {!complaint.ai_analysis && complaint.status === "open" && (
              <Button onClick={onAnalyze} disabled={analyzeLoading} size="sm">
                {analyzeLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                Analyze with AI
              </Button>
            )}
            {complaint.ai_analysis && !complaint.investigation && (
              <Button onClick={onAssignInvestigation} size="sm" variant="outline">
                <Search className="w-4 h-4 mr-2" />
                Assign Investigation
              </Button>
            )}
            {complaint.status !== "closed" && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const reason = prompt("Enter closure reason:");
                  if (reason) onClose(reason);
                }}
              >
                Close
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        <TabsList className="mx-4 mt-2">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="analysis" disabled={!complaint.ai_analysis}>
            AI Analysis
            {complaint.ai_analysis && <Sparkles className="w-3 h-3 ml-1" />}
          </TabsTrigger>
          <TabsTrigger value="investigation" disabled={!complaint.investigation}>
            Investigation
            {complaint.investigation && <Search className="w-3 h-3 ml-1" />}
          </TabsTrigger>
          <TabsTrigger value="letters">Draft Letters</TabsTrigger>
        </TabsList>
        
        <ScrollArea className="flex-1 p-4">
          <TabsContent value="overview" className="mt-0 space-y-4">
            {/* Complainant & Target */}
            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Complainant
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="font-medium">
                    {complaint.complainant_name_text || (complaint.complainant_info ? `${complaint.complainant_info.first_name} ${complaint.complainant_info.last_name}` : "Unknown")}
                  </p>
                  {complaint.complainant_info?.email_primary && (
                    <p className="text-sm text-muted-foreground">{complaint.complainant_info.email_primary}</p>
                  )}
                  {complaint.lot_number && (
                    <p className="text-xs text-muted-foreground mt-1">Lot: {complaint.lot_number}</p>
                  )}
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    Complaint Against
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {complaint.submitted_against?.length > 0 ? (
                    complaint.submitted_against.map((target, i) => (
                      <div key={i}>
                        <p className="font-medium">
                          {target.target_name_text || (complaint.targets_info?.[i]?.type === "owner"
                            ? `${complaint.targets_info[i]?.info?.first_name || ""} ${complaint.targets_info[i]?.info?.last_name || ""}`
                            : complaint.targets_info?.[i]?.info?.role_name || target.role_name || "Unknown")}
                        </p>
                      </div>
                    ))
                  ) : complaint.targets_info?.map((target, i) => (
                    <div key={i}>
                      {target.type === "owner" ? (
                        <p className="font-medium">{target.info?.first_name} {target.info?.last_name}</p>
                      ) : (
                        <p className="font-medium">{target.info?.role_name}</p>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
            
            {/* Description */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Description</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm whitespace-pre-wrap">{complaint.description}</p>
              </CardContent>
            </Card>
            
            {/* Workflow Progress */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Workflow Progress</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${complaint.status !== "open" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>
                    <CheckCircle className="w-3 h-3" />
                    Intake
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${complaint.ai_analysis ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                    {complaint.ai_analysis ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                    AI Analysis
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                    complaint.investigation?.status === "completed" ? "bg-green-100 text-green-700" :
                    complaint.investigation ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-500"
                  }`}>
                    {complaint.investigation?.status === "completed" ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                    Investigation
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                    complaint.linked_violations?.length > 0 ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
                  }`}>
                    {complaint.linked_violations?.length > 0 ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                    Enforcement
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="analysis" className="mt-0 space-y-4">
            {complaint.ai_analysis && (
              <>
                {/* Approval Status */}
                <Card className={complaint.ai_analysis.is_approved ? "border-green-200 bg-green-50" : "border-yellow-200 bg-yellow-50"}>
                  <CardContent className="py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {complaint.ai_analysis.is_approved ? (
                          <>
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            <span className="font-medium text-green-700">Board Approved</span>
                          </>
                        ) : (
                          <>
                            <Clock className="w-5 h-5 text-yellow-600" />
                            <span className="font-medium text-yellow-700">Pending Board Approval</span>
                          </>
                        )}
                      </div>
                      {!complaint.ai_analysis.is_approved && (
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => onApproveAnalysis(false)}>
                            Reject
                          </Button>
                          <Button size="sm" onClick={() => onApproveAnalysis(true)}>
                            Approve
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
                
                {/* Concerns */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Identified Concerns ({complaint.ai_analysis.concerns?.length || 0})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {complaint.ai_analysis.concerns?.map((concern, i) => {
                      const validity = VALIDITY_CONFIG[concern.validity] || VALIDITY_CONFIG.requires_investigation;
                      return (
                        <div key={concern.concern_id} className="border rounded-lg p-3">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm font-medium">{concern.text}</p>
                            <Badge className={validity.color}>{validity.label}</Badge>
                          </div>
                          
                          {concern.citations?.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {concern.citations.map((citation, j) => (
                                <div key={j} className="text-xs bg-gray-50 p-2 rounded">
                                  <span className="font-medium">{citation.document_name}</span>
                                  {citation.section_number && <span> §{citation.section_number}</span>}
                                  {citation.quoted_text && (
                                    <p className="text-muted-foreground mt-1 italic">&quot;{citation.quoted_text}&quot;</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          
                          <div className="mt-2 text-xs text-muted-foreground">
                            Required Action: <span className="font-medium">{concern.required_action?.replace(/_/g, " ")}</span>
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
                
                {/* Board Summary */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Board Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm whitespace-pre-wrap">{complaint.ai_analysis.board_summary}</p>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>
          
          <TabsContent value="investigation" className="mt-0 space-y-4">
            {complaint.investigation && (
              <>
                {/* Investigation Status */}
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm">Investigation Status</CardTitle>
                      <Badge variant="outline">
                        {complaint.investigation.status?.replace(/_/g, " ")}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <p><span className="text-muted-foreground">Assigned To:</span> {complaint.investigation.assigned_to}</p>
                      {complaint.investigation.started_at && (
                        <p><span className="text-muted-foreground">Started:</span> {new Date(complaint.investigation.started_at).toLocaleString()}</p>
                      )}
                      {complaint.investigation.completed_at && (
                        <p><span className="text-muted-foreground">Completed:</span> {new Date(complaint.investigation.completed_at).toLocaleString()}</p>
                      )}
                    </div>
                    
                    {complaint.investigation.status === "not_started" && (
                      <Button className="mt-4" onClick={onStartInvestigation}>
                        Start Investigation
                      </Button>
                    )}
                    
                    {complaint.investigation.status === "in_progress" && (
                      <Button className="mt-4" onClick={onCompleteInvestigation}>
                        Complete Investigation
                      </Button>
                    )}
                  </CardContent>
                </Card>
                
                {/* Tasks */}
                {complaint.investigation.tasks?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Investigation Tasks</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {complaint.investigation.tasks.map((task, i) => (
                          <div key={task.task_id} className="flex items-center gap-3 p-2 bg-gray-50 rounded">
                            {task.status === "completed" ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : task.status === "in_progress" ? (
                              <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                            ) : (
                              <Clock className="w-4 h-4 text-gray-400" />
                            )}
                            <span className="text-sm">{task.task_type?.replace(/_/g, " ")}</span>
                            <Badge variant="outline" className="text-xs ml-auto">{task.status}</Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Findings */}
                {complaint.investigation.findings && (
                  <Card className="border-green-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Investigation Findings</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">Finding:</span>
                        <Badge className={
                          complaint.investigation.findings === "verified" ? "bg-green-100 text-green-700" :
                          complaint.investigation.findings === "unverified" ? "bg-red-100 text-red-700" :
                          "bg-yellow-100 text-yellow-700"
                        }>
                          {complaint.investigation.findings?.replace(/_/g, " ")}
                        </Badge>
                      </div>
                      {complaint.investigation.findings_notes && (
                        <p className="text-sm">{complaint.investigation.findings_notes}</p>
                      )}
                      <div className="flex items-center gap-2 pt-2">
                        <span className="text-sm text-muted-foreground">Recommended Action:</span>
                        <span className="text-sm font-medium">{complaint.investigation.recommended_action?.replace(/_/g, " ")}</span>
                      </div>
                      {complaint.investigation.recommendation_notes && (
                        <p className="text-sm text-muted-foreground">{complaint.investigation.recommendation_notes}</p>
                      )}
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>
          
          <TabsContent value="letters" className="mt-0 space-y-4">
            {complaint.ai_analysis ? (
              <>
                {complaint.ai_analysis.draft_response_to_complainant && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        Draft Response to Complainant
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="bg-gray-50 p-4 rounded-lg text-sm whitespace-pre-wrap">
                        {complaint.ai_analysis.draft_response_to_complainant}
                      </div>
                      <Button className="mt-3" variant="outline" size="sm">
                        <Send className="w-4 h-4 mr-2" />
                        Send Letter
                      </Button>
                    </CardContent>
                  </Card>
                )}
                
                {complaint.ai_analysis.draft_notice_to_subject && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Draft Notice to Subject
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="bg-gray-50 p-4 rounded-lg text-sm whitespace-pre-wrap">
                        {complaint.ai_analysis.draft_notice_to_subject}
                      </div>
                      <Button className="mt-3" variant="outline" size="sm">
                        <Send className="w-4 h-4 mr-2" />
                        Send Notice
                      </Button>
                    </CardContent>
                  </Card>
                )}
                
                {complaint.ai_analysis.internal_board_summary && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Internal Board Summary
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="bg-gray-50 p-4 rounded-lg text-sm whitespace-pre-wrap">
                        {complaint.ai_analysis.internal_board_summary}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>Run AI Analysis to generate draft letters</p>
              </div>
            )}
          </TabsContent>
        </ScrollArea>
      </Tabs>
    </Card>
  );
};

export default ComplaintsPage;
